import React from 'react';

/* ============================================================================
   The primitives every screen is built from.

   · Content lives in soft rounded cards on a tinted ground
   · Anything interactive is filled, tonal or outlined in accent
   · Pills for buttons and chips, so a control never reads as plain text
   · Colour has exactly two jobs: accent = "you can touch this",
     the ageing ramp = "this is how old the money is"

   Ported from the single-tenant implementation. The money and ageing displays
   are not here yet: they need the workspace's own currency formatter, which
   arrives with the tenant context in Session 5.
   ============================================================================ */

export const cx = (...parts: (string | false | null | undefined)[]) =>
    parts.filter(Boolean).join(' ');

/* --------------------------------- Button -------------------------------- */

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'destructive' | 'quiet';
type ButtonSize = 'sm' | 'md' | 'lg';

const BUTTON_VARIANT: Record<ButtonVariant, string> = {
    /* filled */
    primary: 'bg-accent text-on-accent shadow-e1 hover:bg-accent-press active:scale-[.98]',
    /* tonal — the workhorse secondary */
    secondary: 'bg-accent-tint text-accent hover:bg-accent-tint-2 active:scale-[.98]',
    /* text */
    ghost: 'bg-transparent text-label-2 hover:bg-hover hover:text-label',
    /* tonal red: a page-level control that leads to something destructive */
    danger: 'bg-dang-bg text-dang hover:brightness-95 active:scale-[.98]',
    /* filled red: the final answer in a question dialog, the one that does it */
    destructive: 'bg-dang text-card shadow-e1 hover:brightness-95 active:scale-[.98]',
    /* neutral outlined */
    quiet: 'bg-card border border-separator-strong text-label-2 hover:bg-hover hover:text-label',
};

/* `md` is 40px and `lg` 48px: a control a thumb has to hit is never smaller
   than 40, which is what the mobile pass on the reference app settled on. */
const BUTTON_SIZE: Record<ButtonSize, string> = {
    sm: 'h-8 px-3.5 text-[13px] gap-1.5',
    md: 'h-10 px-4.5 text-[14px] gap-2',
    lg: 'h-12 px-6 text-[15px] gap-2',
};

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: ButtonVariant;
    size?: ButtonSize;
    icon?: React.ReactNode;
}

export const Button = ({
    variant = 'secondary',
    size = 'md',
    icon,
    className,
    children,
    ...rest
}: ButtonProps) => (
    <button
        {...rest}
        className={cx(
            'inline-flex items-center justify-center font-semibold whitespace-nowrap rounded-full',
            'transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed disabled:active:scale-100',
            BUTTON_VARIANT[variant],
            BUTTON_SIZE[size],
            className,
        )}
    >
        {icon}
        {children}
    </button>
);

/* --------------------------------- Badge --------------------------------- */

export type Tone = 'neutral' | 'brand' | 'pos' | 'warn' | 'dang' | 'age1' | 'age2' | 'age3' | 'age4';

const TONE: Record<Tone, string> = {
    neutral: 'bg-card-3 text-label-2',
    brand: 'bg-accent-tint text-accent',
    pos: 'bg-pos-bg text-pos',
    warn: 'bg-warn-bg text-warn',
    dang: 'bg-dang-bg text-dang',
    age1: 'bg-age-1-bg text-age-1-ink',
    age2: 'bg-age-2-bg text-age-2-ink',
    age3: 'bg-age-3-bg text-age-3-ink',
    age4: 'bg-age-4-bg text-age-4-ink',
};

export const Badge = ({
    tone = 'neutral',
    children,
    className,
    title,
}: {
    tone?: Tone;
    children: React.ReactNode;
    className?: string;
    title?: string;
}) => (
    <span
        title={title}
        className={cx(
            'inline-flex items-center gap-1 rounded-full px-2 py-[3px]',
            'text-[11.5px] font-semibold leading-tight whitespace-nowrap',
            TONE[tone],
            className,
        )}
    >
        {children}
    </span>
);

/* ---------------------------------- Card --------------------------------- */

/** Grouped card: soft corners, gentle elevation, no hard border. */
export const Card = ({ className, children, ...rest }: React.HTMLAttributes<HTMLDivElement>) => (
    <div {...rest} className={cx('bg-card rounded-[16px] shadow-e1', className)}>
        {children}
    </div>
);

export const SectionHeader = ({
    title,
    subtitle,
    actions,
    className,
}: {
    title: React.ReactNode;
    subtitle?: React.ReactNode;
    actions?: React.ReactNode;
    className?: string;
}) => (
    <div className={cx('flex items-start justify-between gap-4 flex-wrap', className)}>
        <div className="min-w-0">
            <h2 className="text-[17px] font-bold text-label tracking-[-0.02em]">{title}</h2>
            {/* The line under a heading explains; on a phone it costs two or three
                lines of the screen the list needs, and the heading already says it. */}
            {subtitle && <p className="text-[13.5px] text-label-3 mt-1 max-md:hidden">{subtitle}</p>}
        </div>
        {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
    </div>
);

/* ------------------------------ Empty state ------------------------------ */

/**
 * Every screen in a brand-new company shows one of these, and each names the
 * one action that fills it. A blank table with headers and no rows reads as
 * broken software on a customer's first day.
 */
export const EmptyState = ({
    title,
    hint,
    action,
    icon,
}: {
    title: string;
    hint?: string;
    action?: React.ReactNode;
    icon?: React.ReactNode;
}) => (
    <div className="flex flex-col items-center justify-center text-center py-20 px-6">
        {icon && <div className="text-label-3 mb-4">{icon}</div>}
        <p className="text-[17px] font-bold text-label tracking-[-0.02em]">{title}</p>
        {hint && <p className="text-[14px] text-label-3 mt-2 max-w-sm leading-relaxed">{hint}</p>}
        {action && <div className="mt-6">{action}</div>}
    </div>
);

/* ------------------------------- Skeleton -------------------------------- */

/**
 * Placeholder rows while a list is being prepared.
 *
 * Shaped like the rows they stand in for, so the page does not jump when the
 * real ones arrive, and dimmed rather than animated into a stripe show — this
 * appears for a few hundred milliseconds and should not draw the eye.
 */
export const SkeletonRows = ({ rows = 8, className }: { rows?: number; className?: string }) => (
    <div className={cx('divide-y divide-separator', className)} aria-hidden="true">
        {Array.from({ length: rows }).map((_, i) => (
            <div key={i} className="flex items-center gap-4 px-4 py-3.5">
                <div className="flex-1 min-w-0">
                    <div className="h-3.5 rounded bg-card-3" style={{ width: `${38 + ((i * 13) % 34)}%` }} />
                    <div className="h-2.5 rounded bg-card-2 mt-2" style={{ width: `${22 + ((i * 7) % 26)}%` }} />
                </div>
                <div className="h-3.5 w-20 rounded bg-card-3 flex-none" />
                <div className="h-2 w-32 rounded-full bg-card-2 flex-none hidden sm:block" />
                <div className="h-7 w-20 rounded-full bg-card-2 flex-none hidden md:block" />
            </div>
        ))}
    </div>
);

/** Announced to a screen reader; the shapes above are decorative. */
export const LoadingList = ({ label = 'Loading', rows = 8 }: { label?: string; rows?: number }) => (
    <div role="status" aria-live="polite">
        <span className="sr-only">{label}</span>
        <SkeletonRows rows={rows} />
    </div>
);

/* -------------------------------- Loading -------------------------------- */

export const Spinner = ({ className }: { className?: string }) => (
    <svg className={cx('animate-spin', className)} width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" opacity=".2" />
        <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
);
